import { Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Reservation = {
  id: string;
  student_id: string;
  title_name?: string;
  title_code?: string;
  preferred_accession_no?: string | null;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  status: string;
  queue_position: number;
  ready_until?: string | null;
  fulfilled_loan_id?: string | null;
};

export default function LibraryReservationsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentFilter = searchParams.get('student_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canCirculate = Boolean(
    user?.permissions?.includes('education.library.circulate') ||
      user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-library-reservations', studentFilter, status, q],
    queryFn: async () => {
      const res = await api.get<Reservation[]>('/education/library/reservations', {
        params: { student_id: studentFilter, status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const action = useMutation({
    mutationFn: ({ id, operation }: { id: string; operation: 'cancel' | 'ready' | 'fulfill' }) =>
      api.post(`/education/library/reservations/${id}/${operation}`, {}),
    onSuccess: (_result, variables) => {
      message.success(t(`libraryReservation${variables.operation[0].toUpperCase()}${variables.operation.slice(1)}`));
      queryClient.invalidateQueries({ queryKey: ['education-library-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-loans'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('libraryReservations')}
      description={t('libraryReservationsIntro')}
      extra={
        <Space wrap>
          {canCirculate ? (
            <Button type="primary" onClick={() => navigate('/education/library/reservations/new')}>
              {t('newLibraryReservation')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/library')}>{t('backToLibrary')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 150 }}
                value={status}
                onChange={(value) => setStatus(value || undefined)}
                options={['queued', 'ready', 'fulfilled', 'cancelled', 'expired'].map((value) => ({
                  value,
                  label: t(value),
                }))}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(event) => setQ(event.target.value)} allowClear style={{ width: 220 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
            {studentFilter ? (
              <Button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('student_id');
                  setSearchParams(next);
                }}
              >
                {t('clearStudentFilter')}
              </Button>
            ) : null}
          </Form>
        </Card>

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: Reservation) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('title'),
                render: (_: unknown, row: Reservation) =>
                  `${row.title_code ? `${row.title_code} — ` : ''}${row.title_name || ''}`,
              },
              { title: t('accessionNo'), dataIndex: 'preferred_accession_no', width: 130, render: (v) => v || '—' },
              { title: t('queuePosition'), dataIndex: 'queue_position', width: 90 },
              { title: t('readyUntil'), dataIndex: 'ready_until', width: 115, render: (v) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (value: string) => (
                  <Tag color={value === 'ready' ? 'green' : value === 'queued' ? 'processing' : 'default'}>
                    {t(value)}
                  </Tag>
                ),
              },
              {
                title: t('actions'),
                width: 260,
                render: (_: unknown, row: Reservation) =>
                  row.status === 'queued' || row.status === 'ready' ? (
                    <Space size="small" wrap>
                      {canCirculate && row.status === 'queued' ? (
                        <Popconfirm
                          title={t('libraryReadyConfirm')}
                          onConfirm={() => action.mutate({ id: row.id, operation: 'ready' })}
                        >
                          <Button size="small">{t('markReady')}</Button>
                        </Popconfirm>
                      ) : null}
                      {canCirculate && row.status === 'ready' ? (
                        <Popconfirm
                          title={t('libraryFulfillConfirm')}
                          onConfirm={() => action.mutate({ id: row.id, operation: 'fulfill' })}
                        >
                          <Button size="small" type="primary">
                            {t('fulfillAndIssue')}
                          </Button>
                        </Popconfirm>
                      ) : null}
                      {canCirculate ? (
                        <Popconfirm
                          title={t('libraryCancelReservationConfirm')}
                          onConfirm={() => action.mutate({ id: row.id, operation: 'cancel' })}
                        >
                          <Button size="small" danger>
                            {t('cancel')}
                          </Button>
                        </Popconfirm>
                      ) : null}
                    </Space>
                  ) : null,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
